import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useSupabase } from '../hooks/useSupabase';
import type { Database } from '../lib/supabase';

type Interviewer = Database['public']['Tables']['interviewers']['Row'];
type NewInterviewer = Database['public']['Tables']['interviewers']['Insert'];

interface InterviewerContextType {
  interviewers: Interviewer[];
  isLoading: boolean;
  filterInterviewers: (query: string) => void;
  addInterviewer: (interviewer: NewInterviewer) => Promise<void>;
  updateInterviewer: (id: number, updates: Partial<NewInterviewer>) => Promise<void>;
  deleteInterviewer: (id: number) => Promise<void>;
  getInterviewer: (id: number) => Interviewer | undefined;
}

const InterviewerContext = createContext<InterviewerContextType | undefined>(undefined);

export function useInterviewers() {
  const context = useContext(InterviewerContext);
  if (context === undefined) {
    throw new Error('useInterviewers must be used within an InterviewerProvider');
  }
  return context;
}

export function InterviewerProvider({ children }: { children: React.ReactNode }) {
  const [interviewers, setInterviewers] = useState<Interviewer[]>([]);
  const [filteredInterviewers, setFilteredInterviewers] = useState<Interviewer[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const { 
    fetchInterviewers, 
    createInterviewer, 
    updateInterviewer: updateSupabaseInterviewer,
    deleteInterviewer: deleteSupabaseInterviewer 
  } = useSupabase();

  useEffect(() => {
    loadInterviewers();
  }, []);

  async function loadInterviewers() {
    try {
      const data = await fetchInterviewers();
      setInterviewers(data || []);
      setFilteredInterviewers(data || []);
    } catch (error) {
      console.error('Error loading interviewers:', error);
      toast.error('Failed to load interviewers');
    } finally {
      setIsLoading(false);
    }
  }

  const addInterviewer = async (newInterviewer: NewInterviewer) => {
    try {
      if (!newInterviewer.url) {
        throw new Error('Interviewer URL is required');
      }

      const createdInterviewer = await createInterviewer(newInterviewer);
      
      if (createdInterviewer) {
        // Update both the full list and filtered list
        setInterviewers(prev => [createdInterviewer, ...prev]);
        setFilteredInterviewers(prev => [createdInterviewer, ...prev]);
      } else {
        throw new Error('Failed to create interviewer - no response from server');
      }
    } catch (error) {
      console.error('Error creating interviewer:', error);
      throw new Error(
        error instanceof Error 
          ? error.message 
          : 'Failed to create interviewer - please try again'
      );
    }
  };

  const updateInterviewer = async (id: number, updates: Partial<NewInterviewer>) => {
    try {
      const data = await updateSupabaseInterviewer(id, updates);
      
      if (data) {
        setInterviewers(prev => prev.map(interviewer => 
          interviewer.id === id ? data : interviewer
        ));
        toast.success('Interviewer updated successfully');
      }
    } catch (error) {
      console.error('Error updating interviewer:', error);
      toast.error('Failed to update interviewer');
      throw error;
    }
  };

  const deleteInterviewer = async (id: number) => {
    try {
      await deleteSupabaseInterviewer(id);
      // Update both the full list and filtered list
      const updatedInterviewers = interviewers.filter(interviewer => interviewer.id !== id);
      setInterviewers(updatedInterviewers);
      setFilteredInterviewers(prev => prev.filter(interviewer => interviewer.id !== id));
      toast.success('Interviewer deleted successfully');
    } catch (error) {
      console.error('Error deleting interviewer:', error);
      toast.error('Failed to delete interviewer');
      throw error;
    }
  };

  const filterInterviewers = (query: string) => {
    setSearchQuery(query);
    if (!query.trim()) {
      // Maintain the same order as the main list
      setFilteredInterviewers([...interviewers]);
      return;
    }

    const lowercaseQuery = query.toLowerCase();
    const filtered = [...interviewers].filter(interviewer => 
      interviewer.name.toLowerCase().includes(lowercaseQuery) ||
      interviewer.company.toLowerCase().includes(lowercaseQuery) ||
      interviewer.instructions?.toLowerCase().includes(lowercaseQuery)
    );
    setFilteredInterviewers(filtered);
  };

  const getInterviewer = (id: number) => {
    return interviewers.find(interviewer => interviewer.id === id);
  };

  return (
    <InterviewerContext.Provider value={{ 
      interviewers: filteredInterviewers,
      isLoading,
      addInterviewer, 
      updateInterviewer,
      deleteInterviewer, 
      getInterviewer,
      filterInterviewers
    }}>
      {children}
    </InterviewerContext.Provider>
  );
}